/* =============================================================================
   ValidationService - declarative field rules resolved from data-attributes and
   component options, plus the shared invalid-state marker.
   Deliberate semantics, all of them contract:
     - validateField() short-circuits on the FIRST failing rule;
     - required is detected from the `required` ATTRIBUTE, or from an EXACT
       class="required" match - a class-token match is opt-in per call, so a
       field carrying other classes is not required unless it asks to be;
     - on a <select>, the empty-choice convention ('' / '0' / the "select one"
       caption) counts as EMPTY;
     - an unknown rule name warns and is SKIPPED - never a throw, never a
       silent pass of the whole field.
   An invalid field carries the `errorFieldBorder` class; resetAll(root) clears
   every marker under a root, so passing a window element resets just that window.
   Known limitation: no message bubble is rendered here - the marker and the
   returned {valid, message, rule} are the whole surface; painting the message
   belongs to the component that shows it.
   Built-in rules: required · numeric · min · max · email. Rule messages come
   from the localisation map (i18n/l10n/localisation-map.js).
   ============================================================================= */
import { t } from '../i18n/l10n/localisation-map.js';

const _rules = new Map();

export const ValidationService = {
  /** register a rule: name -> ({value, el, param}) => true | message-string */
  addRule(name, fn) { _rules.set(name, fn); },

  rule(name) { return _rules.get(name) || null; },

  /**
   * Validate one field. rules: [{name, param, message}] - the first failure
   * short-circuits. Returns {valid, message, rule}; a rule's own `message` wins
   * over whatever text the rule function returned.
   */
  validateField(el, value, rules) {
    for (const r of rules || []) {
      const fn = _rules.get(r.name);
      if (!fn) { console.warn('[ics] ValidationService: unknown rule', r.name); continue; }
      const out = fn({ value, el, param: r.param });
      if (out !== true) return { valid: false, message: r.message || (typeof out === 'string' ? out : t('validation.required')), rule: r.name };
    }
    return { valid: true, message: '', rule: null };
  },

  /** the invalid marker (errorFieldBorder), and reset-all under any root element */
  markInvalid(el, on = true) { el.classList.toggle('errorFieldBorder', on); },
  resetAll(root = document) {
    root.querySelectorAll('.errorFieldBorder').forEach((el) => el.classList.remove('errorFieldBorder'));
  },

  /**
   * Required detection on an element: presence of the `required` attribute, or
   * an EXACT class="required" - a class-token match only when opted in.
   */
  isRequired(el, { tokenMatch = false } = {}) {
    if (el.hasAttribute('required')) return true;
    const cls = el.getAttribute('class') || '';
    return tokenMatch ? el.classList.contains('required') : cls === 'required';
  },
};

/* ---- built-in rules -------------------------------------------------------- */
ValidationService.addRule('required', ({ value, el }) => {
  if (el && el.tagName === 'SELECT') {
    // the empty-choice convention ('' / '0' / the "select one" caption) counts as EMPTY
    return !(value === '' || value === '0' || value === 0 || value === t('combo.selectOne')) || t('validation.required');
  }
  return String(value ?? '').trim() !== '' || t('validation.required');
});
/* the numeric rules READ through THE number definition (Ics.number, components/
   ics-numberbox.js) - resolved at call time through the page, never imported: this
   module rides into several bundles through core/mixins.js, and an import would
   re-register the numberbox in each of them. There is no second number parse
   here; a numeric rule validated before dist-v2/ics-textbox.min.js has published
   Ics.number throws the error below rather than guessing. */
const _icsNumber = () => {
  const n = globalThis.Ics && globalThis.Ics.number;
  if (!n) throw new Error('[ics] Ics.number is not published - dist-v2/ics-textbox.min.js must load before a numeric rule is validated');
  return n;
};
ValidationService.addRule('numeric', ({ value }) => value === '' || _icsNumber().parseRaw(value) !== null || 'Must be a number.');
ValidationService.addRule('min', ({ value, param }) => value === '' || _icsNumber().parse(value) >= param || 'Value too small.');
ValidationService.addRule('max', ({ value, param }) => value === '' || _icsNumber().parse(value) <= param || 'Value too large.');
ValidationService.addRule('email', ({ value }) => value === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) || 'Invalid email.');
